import type * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { PropertyFilterProps } from '@cloudscape-design/components/property-filter';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import SpaceBetween from '@cloudscape-design/components/space-between';
import {
  PLATFORM_USER_SEARCH_OPERATORS,
  PLATFORM_USER_SEARCH_PROPERTY_KEYS,
  type TenantRole,
  type Member,
  type PlatformUserRow,
  type PlatformUserSearchOperator,
  type PlatformUserSearchPropertyKey,
  type SearchPlatformUsersInput,
} from '@etiquetachef/types';
import { toast } from '@/lib/notifications';
import { removeUserFromTenantCallable } from '@/lib/callables';
import { AdminConfirm } from '@/components/ui/AdminConfirm';
import { MemberRoleDialog } from '@/features/members/MemberRoleDialog';
import { MemberCredentialDialog } from '@/features/members/MemberCredentialDialog';
import { useUsersSearch } from '@/features/platformUsers/useUsersSearch';
import { UserList, type UserAction } from '@/features/platformUsers/UserList';
import {
  ContactVerificationDialog,
  type ContactVerificationTarget,
} from '@/features/platformUsers/ContactVerificationDialog';
import {
  AccountActionDialog,
  type AccountAction,
} from '@/features/platformUsers/AccountActionDialog';
import { TenantPickerDialog } from '@/features/platformUsers/TenantPickerDialog';

const KNOWN_KEYS = new Set<string>(PLATFORM_USER_SEARCH_PROPERTY_KEYS);
const KNOWN_OPERATORS = new Set<string>(PLATFORM_USER_SEARCH_OPERATORS);
const DEFAULT_PAGE_SIZE = 25;

/** Ações que operam sobre uma MEMBERSHIP e por isso exigem escolher a empresa. */
const TENANT_SCOPED: ReadonlySet<UserAction> = new Set(['role', 'credential', 'remove']);

/**
 * Tela global de Usuários (`/users`, grupo Empresas) — issue #257.
 *
 * Complementa a aba "Usuários" do detalhe do tenant: ali o recorte é a
 * empresa; aqui é a **pessoa**, com as empresas dela em uma coluna. É por onde
 * o suporte atende quem liga sem saber dizer em qual empresa está.
 *
 * Origem: #257 — commit b3bc5df63, 2026-08-11 (tela global de Usuários +
 * correção do gate de contato).
 *
 * Contrato: tokens do `PropertyFilter` → `SearchPlatformUsersInput` (mesmo
 * filtro 1:1 da lista de tenants) → callable `searchPlatformUsers` via
 * `useUsersSearch`. Ações por pessoa (verificar contato, bloquear, revogar
 * sessões) abrem os diálogos próprios. Ações por MEMBERSHIP (papel,
 * credencial, remover) exigem a empresa: com uma só, vai direto; com várias,
 * o `TenantPickerDialog` pergunta. Remover chama `removeUserFromTenant`.
 *
 * Usado por: rota `users` (`routes/router.tsx`), menu Empresas
 * (`AdminLayout`). Spec: `docs/screens/platform-users-admin.md`.
 *
 * Arquitetura: os diálogos de papel/credencial são os MESMOS da aba Usuários
 * do tenant (`features/members/`), com os guard-rails do último OWNER e o
 * bypass de super-admin; a revalidação é das callables.
 *
 * Falhas conhecidas:
 *  - 2026-10-25 (#1021): a recusa da remoção ia só por toast, atrás do overlay
 *    do confirm (que fica aberto no erro). Hoje é `Alert` dentro do confirm.
 *
 * Issues: #257 (origem), #1021 (erro dentro do modal; mesmo achado do CRM, #174).
 *
 * Histórico:
 *  - 2026-10-25 15:00 +0100 — (#1021) erro da remoção em `Alert` no confirm.
 */
export function UsersPage(): React.JSX.Element {
  const { t } = useTranslation();
  const [query, setQuery] = useState<PropertyFilterProps.Query>({
    tokens: [],
    operation: 'and',
  });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [refreshNonce, setRefreshNonce] = useState(0);

  const [verifyTarget, setVerifyTarget] = useState<ContactVerificationTarget | null>(null);
  const [accountAction, setAccountAction] = useState<AccountAction | null>(null);
  const [accountUser, setAccountUser] = useState<PlatformUserRow | null>(null);
  const [pending, setPending] = useState<{ action: UserAction; user: PlatformUserRow } | null>(
    null,
  );
  const [scoped, setScoped] = useState<{
    action: UserAction;
    user: PlatformUserRow;
    tenantId: string;
  } | null>(null);
  const [removing, setRemoving] = useState(false);
  // #1021 (2026-10-25 15:00 +0100): recusa da remoção, mostrada dentro do confirm.
  const [removeError, setRemoveError] = useState<string | null>(null);

  const tokens = useMemo(
    () =>
      query.tokens
        .filter(
          (tk) =>
            typeof tk.propertyKey === 'string' &&
            KNOWN_KEYS.has(tk.propertyKey) &&
            KNOWN_OPERATORS.has(tk.operator) &&
            tk.value != null &&
            String(tk.value) !== '',
        )
        .map((tk) => ({
          propertyKey: tk.propertyKey as PlatformUserSearchPropertyKey,
          operator: tk.operator as PlatformUserSearchOperator,
          value: String(tk.value),
        })),
    [query],
  );

  const input: SearchPlatformUsersInput = useMemo(
    () => ({ tokens, page, pageSize }),
    [tokens, page, pageSize],
  );

  const state = useUsersSearch(input, refreshNonce);
  const errorMessage = state.kind === 'ERROR' ? t('adminPlatformUsers.errors.searchFailed') : undefined;

  useEffect(() => {
    if (errorMessage) toast.error(errorMessage);
  }, [errorMessage]);

  const refresh = (): void => {
    setRefreshNonce((n) => n + 1);
  };

  function handleAction(action: UserAction, user: PlatformUserRow): void {
    switch (action) {
      case 'verifyEmail':
        setVerifyTarget({ user, channel: 'EMAIL', verified: true });
        return;
      case 'unverifyEmail':
        setVerifyTarget({ user, channel: 'EMAIL', verified: false });
        return;
      case 'verifyPhone':
        setVerifyTarget({ user, channel: 'PHONE', verified: true });
        return;
      case 'unverifyPhone':
        setVerifyTarget({ user, channel: 'PHONE', verified: false });
        return;
      case 'block':
      case 'unblock':
      case 'revokeSessions':
        setAccountUser(user);
        setAccountAction(action);
        return;
      // Papel, credencial e remoção caem no fluxo por-empresa abaixo.
      case 'role':
      case 'credential':
      case 'remove':
        break;
    }

    if (!TENANT_SCOPED.has(action)) return;
    // Papel, credencial e remoção valem por EMPRESA. Com mais de uma, aplicar
    // na primeira seria escolher pelo operador — e errado em silêncio.
    // #1021: o confirm de remoção abre sem o erro da tentativa anterior.
    setRemoveError(null);
    if (user.memberships.length === 1) {
      const tenantId = user.memberships[0]?.tenantId ?? '';
      setScoped({ action, user, tenantId });
    } else {
      setPending({ action, user });
    }
  }

  async function handleRemove(): Promise<void> {
    if (!scoped) return;
    setRemoving(true);
    setRemoveError(null);
    try {
      await removeUserFromTenantCallable({
        tenantId: scoped.tenantId,
        targetUid: scoped.user.uid,
      });
      toast.success(t('adminMembers.toast.removed'));
      setScoped(null);
      refresh();
    } catch (err) {
      const code = err instanceof Error ? err.message : String(err);
      // Auditoria 2026-10-25 15:00 +0100 (#1021): era `toast.error`, atrás do overlay do confirm, que
      // não fecha no erro (mesmo achado do CRM, #174). Vai para o `Alert` dentro dele.
      setRemoveError(
        code.includes('failed-precondition')
          ? t('adminMembers.errors.cannotRemove')
          : t('adminMembers.errors.removeFailed'),
      );
    } finally {
      setRemoving(false);
    }
  }

  // Os diálogos de membro (papel/credencial) são os MESMOS da aba do tenant —
  // já tratam o bypass de super-admin e os guard-rails do último OWNER. Aqui só
  // adaptamos a linha da pessoa para o shape `Member` da empresa escolhida.
  const scopedMember: Member | null = useMemo(() => {
    if (!scoped) return null;
    const membership = scoped.user.memberships.find((m) => m.tenantId === scoped.tenantId);
    if (!membership) return null;
    return {
      uid: scoped.user.uid,
      tenantId: scoped.tenantId,
      role: membership.role as TenantRole,
      displayName: scoped.user.displayName,
      email: scoped.user.email,
      joinedAt: membership.joinedAt,
    } as Member;
  }, [scoped]);

  return (
    <>
      <UserList
        users={state.kind === 'READY' ? state.items : []}
        total={state.kind === 'READY' ? state.total : 0}
        truncated={state.kind === 'READY' && state.truncated}
        loading={state.kind === 'LOADING'}
        errorMessage={errorMessage}
        query={query}
        onChangeQuery={(q): void => {
          setQuery(q);
          setPage(1);
        }}
        currentPageIndex={page}
        pageSize={pageSize}
        onChangePage={setPage}
        onChangePageSize={setPageSize}
        onRefresh={refresh}
        onAction={handleAction}
      />

      <ContactVerificationDialog
        target={verifyTarget}
        onClose={(): void => {
          setVerifyTarget(null);
        }}
        onDone={refresh}
      />

      <AccountActionDialog
        action={accountAction}
        user={accountUser}
        onClose={(): void => {
          setAccountAction(null);
          setAccountUser(null);
        }}
        onDone={refresh}
      />

      <TenantPickerDialog
        user={pending?.user ?? null}
        onClose={(): void => {
          setPending(null);
        }}
        onSelect={(tenantId): void => {
          if (pending) {
            setRemoveError(null);
            setScoped({ action: pending.action, user: pending.user, tenantId });
          }
          setPending(null);
        }}
      />

      <MemberRoleDialog
        tenantId={scoped?.tenantId ?? ''}
        member={scoped?.action === 'role' ? scopedMember : null}
        onClose={(): void => {
          setScoped(null);
          refresh();
        }}
      />

      <MemberCredentialDialog
        tenantId={scoped?.tenantId ?? ''}
        member={scoped?.action === 'credential' ? scopedMember : null}
        onClose={(): void => {
          setScoped(null);
        }}
      />

      <AdminConfirm
        visible={scoped?.action === 'remove'}
        title={t('adminMembers.removeDialog.title')}
        confirmLabel={t('adminMembers.removeDialog.confirm')}
        loading={removing}
        onConfirm={(): void => {
          void handleRemove();
        }}
        onDismiss={(): void => {
          if (removing) return;
          setRemoveError(null);
          setScoped(null);
        }}
      >
        <SpaceBetween size="m">
          <Box variant="p">
            {t('adminMembers.removeDialog.body', { name: scoped?.user.displayName ?? '' })}
          </Box>
          {/* Auditoria 2026-10-25 15:00 +0100 (#1021): erro dentro do confirm, junto do footer (#174). */}
          {removeError !== null && <Alert type="error">{removeError}</Alert>}
        </SpaceBetween>
      </AdminConfirm>
    </>
  );
}
